import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const BigDataAndAnalytics = () => {
  const phrases = [
    "Distributed Computation",
    "Sub-Second Analytics",
    "Predictive Intelligence"
  ];

  const cardData = [
    { type: 'purple-clay', content: '📊' },
    { type: 'smooth-wave', content: '⚡' },
    { type: 'abstract-blocks', content: '🔮' },
    { type: 'pink-sphere', content: '🗄️' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🗄️", title: "Enterprise Data Lakehouse Architecture", desc: "Unify batch and real-time structured, semi-structured, and unstructured telemetry paths into modern, ACID-compliant open storage layers." },
    { icon: "⚡", title: "Sub-Second Real-Time Streaming Engines", desc: "Build low-latency event-driven routing grids to process, aggregate, and alert on clickstream telemetry and transactional messages as they happen." },
    { icon: "🧮", title: "Massive Distributed Compute Optimizations", desc: "Configure cluster kernels, partition boundaries, and distributed query logic to scale computations cleanly past petabyte thresholds." },
    { icon: "🧠", title: "Predictive Modeling & Applied ML Integration", desc: "Deploy automated machine learning inference pipelines, statistical forecasting modules, and high-performance ML feature stores." },
    { icon: "🛡️", title: "Automated Data Governance & Lineage", desc: "Implement programmatic cataloging systems, dynamic column-level tokenization, and strict end-to-end data history auditing models." },
    { icon: "🔌", title: "Ecosystem ELT/ETL Injection Pipelines", desc: "Construct declarative, fault-tolerant orchestration scripts that pull information seamlessly from production tools, databases, and APIs." },
    { icon: "📊", title: "Immersive Visual BI Operations Cockpits", desc: "Develop interactive, rapid-response enterprise dashboards providing actionable insights directly to operational stakeholders." }
  ];

  const advancedServices = [
    { icon: "🤖", title: "Continuous MLOps Pipeline Automation", desc: "Establish continuous re-training frameworks that automatically evaluate classifier drift and push fresh models to edge servers." },
    { icon: "🔮", title: "Vector Embeddings & Semantic Search Pools", desc: "Assemble dynamic data processing structures to populate high-dimensional vector databases for fast generative AI search." },
    { icon: "⚖️", title: "Regulatory Masking & PII Boundary Rules", desc: "Enforce automated classification routines that discover and encrypt highly personal consumer markers dynamically." },
    { icon: "🌍", title: "Global Multi-Region Data Mesh Fabrics", desc: "Decouple big data architectures into specialized domain nodes, enabling localized autonomy while maintaining global catalog consensus." },
    { icon: "⏳", title: "Hyper-Scale Time-Series Aggregations", desc: "Fine-tune localized database setups to parse billions of tracking entries from connected industrial sensors down to the millisecond." },
    { icon: "💾", title: "Dynamic Cost-Tier Lifecycle Policies", desc: "Track usage telemetry to partition older database entities securely onto ultra-low-cost storage pools automatically." },
    { icon: "🛠️", title: "Algorithmic Database Query Optimizations", desc: "Analyze slow table scans and optimize indexes to accelerate complex calculation speeds across massive tables." },
    { icon: "🔗", title: "B2B Secure Clean Room Environments", desc: "Deploy encrypted runtime sandboxes that allow shared cross-entity data matching without revealing private core tables." },
    { icon: "🧪", title: "Automated Data Quality Assertions", desc: "Execute automated validation checks on incoming information to catch anomalies and flag bad records before they hit production dashboards." }
  ];

  const technologies = {
    "Storage & Compute Kernels": ["Apache Spark / Databricks", "Delta Lake / Apache Iceberg", "Snowflake Data Warehouses", "Trino / Presto Query Engines"],
    "Streaming & Event Pipelines": ["Apache Kafka / Confluent Hub", "Apache Flink Stream Processor", "AWS Kinesis / GCP PubSub", "RabbitMQ Messaging Grids"],
    "Data Science & AI Frameworks": ["Python (Pandas / PyTorch)", "Scikit-Learn / MLflow", "HuggingFace Ecosystem", "Pinecone / Milvus Vector DBs"],
    "Orchestration & Governance": ["Apache Airflow / Prefect", "dbt (Data Build Tool)", "Apache Atlas Metadata Hub", "Collibra Governance Suites"],
    "Cloud & Infrastructure DevOps": ["AWS EMR / Google Dataproc", "Kubernetes Spark Operator", "Terraform Data Infrastructure", "Docker Container Systems"]
  };

  const processSteps = [
    { num: "01", title: "Data Discovery & Audit", desc: "Evaluating active database silos, profiling system schemas, and uncovering computational bottlenecks." },
    { num: "02", title: "Topology & Lakehouse Design", desc: "Structuring data access domains, mapping partition keys, and laying out structural schema rules." },
    { num: "03", title: "Data Ingestion Sprinting", desc: "Programming real-time streaming channels and constructing resilient pipeline layers across core domains." },
    { num: "04", title: "Compute & Transformation Setup", desc: "Writing optimized database scripts, assembling feature calculations, and setting up automated scheduling pipelines." },
    { num: "05", title: "Governance & Security Hardening", desc: "Applying strict access privileges, dynamic data masking rules, and setting up end-to-end trace tracking." },
    { num: "06", title: "Production Cluster Deployment", desc: "Migrating enterprise data paths onto highly elastic production platforms with zero impact on active business loops." },
    { num: "07", title: "Operational Performance Tuning", desc: "Evaluating compute costs, tracking query response speeds, and continuously refining machine learning modules." }
  ];

  const industries = [
    "Enterprise FinTech & Quantitative Banking", "High-Volume E-Commerce Networks", "Healthcare Informatics & Genomic Labs",
    "Smart Telecom Networks", "Logistics Control & IoT Telematics", "Energy Grids & Smart Meter Operators",
    "Digital AdTech & Real-Time Bidding Platforms", "SaaS Operations & Product Telemetry Hubs", "Media Streaming & Content Networks",
    "Aerospace Systems & Industrial Factories", "Smart Cities & Urban Planning Networks", "Retail Analytics & Supply Chains"
  ];

  const designBenefits = [
    "Sub-Second Analytical Query Speeds", "Unified Single Source of Truth Views", "Elastic Multi-Petabyte Compute Scales",
    "Airtight Zero-Trust Governance Controls", "Data-Driven Forecasting Precision", "Drastic Cloud Infrastructure Cost Reductions",
    "Rapid Machine Learning Deployment Loops", "100% Auditable Compliance Paths"
  ];

  const whyChooseUs = [
    "Certified Data Architects & Engineers", "Proven MVP-to-Petabyte Scale Frameworks", "Modern UI/UX Analytics Performance",
    "Deep Hybrid Cloud Infrastructure Integration", "Security-First Encrypted Data Boundaries", "High-Velocity Computation Pipelines",
    "Comprehensive Post-Launch Maintenance Support", "Transparent Data Migration Models", "Enterprise-Grade SLA Commitments",
    "Experienced Cloud Operations Designers", "Agile Co-Development Methodology", "100% Core Computational Stability Commitments"
  ];

  return (

    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Big Data & Analytics Services | Data Engineering & Business Intelligence | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Big Data & Analytics Services | Data Engineering & Business Intelligence | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides Big Data and Analytics services, including data engineering, business intelligence, data warehousing, predictive analytics, real-time analytics, AI-driven insights, data visualization, and enterprise analytics solutions."
        />

        <meta
          name="keywords"
          content="Big Data, Big Data Analytics, Big Data Services, Data Analytics, Business Intelligence, Data Engineering, Data Warehousing, Predictive Analytics, Real-Time Analytics, Data Visualization, Data Science, AI Analytics, Machine Learning Analytics, Enterprise Analytics, Data Management, Business Analytics, Big Data Solutions, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/big-data-and-analytics"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Big Data & Analytics Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Unlock the value of your data with Big Data and Analytics services from Affsoft IT Solutions Pvt. Ltd. We specialize in data engineering, business intelligence, predictive analytics, AI-powered insights, and enterprise analytics solutions."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/big-data-and-analytics"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Big Data & Analytics Services | Data Engineering & Business Intelligence | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Transform business data into actionable insights with Big Data and Analytics services from Affsoft IT Solutions Pvt. Ltd. We deliver data engineering, BI, predictive analytics, AI-driven analytics, and data visualization solutions."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="big-data-analytics-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#130d22' // Tech-centric deep violet context fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Big Data &nbsp; and &nbsp;</span>
              <span>Predictive Analytics Solutions </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-violet fs-3" style={{ color: '#a855f7' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">Big Data & Analytics</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we transform massive raw data silos into clear business value. Our advanced Big Data and Analytics engineering solutions combine modern real-time data streaming, elastic compute clusters, and rigorous governance systems to turn high-velocity data pipelines into predictive insight engines at scale.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Audit Your Data Estate</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#a855f7' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#a855f7' }}>Information Architecture</span>
              <h2 className="fw-bold text-dark display-6">Unify High-Velocity Data Infrastructure for Advanced Scaling</h2>
              <p className="text-muted mt-3">
                Disconnected application pools, sluggish data warehouses, and manual quality checks create massive security risks and stall key strategic updates. At Affsoft IT Solutions Pvt. Ltd., we engineer robust, automated data platforms built for performance.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#a855f7' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Strategic Gains of Highly Scalable Big Data Platforms</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#a855f7' }}>Computation Engineering</span>
              <h2 className="fw-bold text-dark display-6">High-Yield Processing Layers Built for Absolute Visibility</h2>
              <p className="text-muted mt-3">
                We extend standard database networks into highly optimized information pipelines. From deploying low-latency event processors to managing multi-region clusters, our frameworks guarantee complete operational control.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#a855f7' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#c084fc' }}>Tool Integration Matrix</span>
              <h2 className="fw-bold display-6">Enterprise Infrastructure & Compute Toolsets We Leverage</h2>
              <p className="text-light-50">We structure, deploy, and monitor heavy engineering architectures using trusted global core systems.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#c084fc' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#c084fc' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Implementation Steps</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Data Roadmap</h2>
              <p className="text-muted">A clear, disciplined pipeline framework designed to move your analytics architecture safely from initial discovery to active production tuning.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#a855f7' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#a855f7' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine math-tested computation systems with extensive cloud experience to protect your core data assets.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#a855f7' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>

    </>
  );
};

export default BigDataAndAnalytics;